import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clientsApi } from '../../api/client';
import { Client, ClientHistory, ClientHistoryProject, ClientHistoryInvoice } from '../../types';
import {
  Plus, Search, Grid3X3, List, X, Mail, Phone,
  MapPin, Calendar, FolderKanban, Edit2, User, Save,
  BarChart3, FileText, ChevronRight, TrendingUp, Clock, CheckCircle2,
  AlertCircle,
} from 'lucide-react';

// ─── Design tokens ───────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  borderHover: '#2e2e42',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
};

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_CLIENTS: Client[] = [
  { id: 1, name: 'Carlos García', company: 'Moda Express SL', email: 'carlos@modaexpress.es', phone: '612 345 678', city: 'Madrid', nif: 'B12345678', address: 'Calle Gran Vía 10', postalCode: '28013', country: 'España', notes: 'Cliente habitual de DTF. Paga al contado.', createdAt: new Date(Date.now() - 90 * 86400000).toISOString() },
  { id: 2, name: 'Ana Torres', company: 'Tech Startup SL', email: 'ana@techstartup.es', phone: '623 456 789', city: 'Barcelona', nif: 'B98765432', address: 'Paseo de Gracia 55', postalCode: '08007', country: 'España', notes: '', createdAt: new Date(Date.now() - 60 * 86400000).toISOString() },
  { id: 3, name: 'Luis Fernández', company: 'Expo & Trade', email: 'luis@expotrade.es', phone: '634 567 890', city: 'Valencia', nif: 'B11223344', address: 'Av. del Port 22', postalCode: '46023', country: 'España', notes: 'Cliente para ferias. Plazos ajustados.', createdAt: new Date(Date.now() - 45 * 86400000).toISOString() },
  { id: 4, name: 'Elena Romero', company: 'Eventos Premium SL', email: 'elena@eventospremium.es', phone: '645 678 901', city: 'Sevilla', nif: 'B55667788', address: 'Calle Sierpes 8', postalCode: '41004', country: 'España', notes: '', createdAt: new Date(Date.now() - 30 * 86400000).toISOString() },
  { id: 5, name: 'Roberto Silva', company: 'Consultoría Norte', email: 'roberto@consultorianorte.es', phone: '656 789 012', city: 'Bilbao', nif: 'B33445566', address: 'Gran Vía 42', postalCode: '48009', country: 'España', notes: 'Corporativo. Requiere factura.', createdAt: new Date(Date.now() - 10 * 86400000).toISOString() },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatEuro(n: number) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}

const AVATAR_COLORS = ['#f97316', '#3b82f6', '#8b5cf6', '#ec4899', '#22c55e', '#14b8a6'];
function avatarColor(id: number) { return AVATAR_COLORS[id % AVATAR_COLORS.length]; }

// ─── Input style ──────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0a0a0f', border: `1px solid ${C.border}`,
  borderRadius: 8, color: C.textPrimary, fontSize: 13, padding: '10px 12px',
  fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: C.textMuted, letterSpacing: '0.06em',
  fontFamily: 'JetBrains Mono, monospace', display: 'block', marginBottom: 6,
};

// ─── Status helpers ───────────────────────────────────────────────────────────

const PROJECT_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  pending:       { label: 'Pendiente',    color: C.textSecondary, bg: 'rgba(107,104,128,0.12)' },
  in_progress:   { label: 'En progreso',  color: C.info,          bg: 'rgba(59,130,246,0.12)' },
  ready_to_pack: { label: 'Listo embalar',color: C.warning,       bg: 'rgba(245,158,11,0.12)' },
  done:          { label: 'Completado',   color: C.success,       bg: 'rgba(34,197,94,0.12)' },
  cancelled:     { label: 'Cancelado',    color: C.error,         bg: 'rgba(239,68,68,0.12)' },
};

const INVOICE_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  draft:     { label: 'Borrador',  color: C.textSecondary, bg: 'rgba(107,104,128,0.12)' },
  sent:      { label: 'Enviada',   color: C.info,          bg: 'rgba(59,130,246,0.12)' },
  paid:      { label: 'Pagada',    color: C.success,       bg: 'rgba(34,197,94,0.12)' },
  cancelled: { label: 'Cancelada', color: C.error,         bg: 'rgba(239,68,68,0.12)' },
};

function StatusBadge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 9px', borderRadius: 20,
      background: bg, color, fontSize: 11, fontWeight: 600,
      fontFamily: 'DM Sans, sans-serif',
    }}>
      {label}
    </span>
  );
}

// ─── Client Card (grid) ───────────────────────────────────────────────────────

function ClientGridCard({ client, onClick }: { client: Client; onClick: () => void }) {
  const color = avatarColor(client.id);
  return (
    <div
      onClick={onClick}
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
        padding: '20px', cursor: 'pointer', transition: 'all 0.15s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.border = `1px solid ${C.borderHover}`; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.border = `1px solid ${C.border}`; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
    >
      {/* Avatar + name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: `${color}22`, border: `2px solid ${color}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color, fontFamily: 'Syne, sans-serif', flexShrink: 0 }}>
          {getInitials(client.name)}
        </div>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name}</div>
          {client.company && <div style={{ fontSize: 12, color: C.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.company}</div>}
        </div>
      </div>

      {/* Details */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {client.email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Mail size={12} color={C.textMuted} />
            <span style={{ fontSize: 12, color: C.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.email}</span>
          </div>
        )}
        {client.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Phone size={12} color={C.textMuted} />
            <span style={{ fontSize: 12, color: C.textSecondary }}>{client.phone}</span>
          </div>
        )}
        {client.city && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <MapPin size={12} color={C.textMuted} />
            <span style={{ fontSize: 12, color: C.textSecondary }}>{client.city}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Calendar size={11} color={C.textMuted} />
          <span style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>{formatDate(client.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Client List Row ──────────────────────────────────────────────────────────

function ClientListRow({ client, onClick }: { client: Client; onClick: () => void }) {
  const color = avatarColor(client.id);
  return (
    <tr onClick={onClick} style={{ cursor: 'pointer', transition: 'background 0.1s' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
      <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: `${color}22`, border: `2px solid ${color}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color, fontFamily: 'Syne, sans-serif', flexShrink: 0 }}>
            {getInitials(client.name)}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{client.name}</div>
            {client.company && <div style={{ fontSize: 11, color: C.textSecondary }}>{client.company}</div>}
          </div>
        </div>
      </td>
      <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Mail size={12} color={C.textMuted} />
          <span style={{ fontSize: 12, color: C.textSecondary }}>{client.email || '—'}</span>
        </div>
      </td>
      <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
        <span style={{ fontSize: 12, color: C.textSecondary }}>{client.phone || '—'}</span>
      </td>
      <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
        <span style={{ fontSize: 12, color: C.textSecondary }}>{client.city || '—'}</span>
      </td>
      <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
        <span style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>{formatDate(client.createdAt)}</span>
      </td>
    </tr>
  );
}

// ─── Client Modal (create / edit / view) ─────────────────────────────────────

type ModalMode = 'view' | 'edit' | 'create';

function ClientModal({ client, mode: initMode, onClose, onSaved, isMobile }: { client?: Client; mode: ModalMode; onClose: () => void; onSaved: (c: Client) => void; isMobile: boolean }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<ModalMode>(initMode);
  const [form, setForm] = useState<Partial<Client>>(client || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = mode === 'edit' || mode === 'create';
  const color = client ? avatarColor(client.id) : C.orange;

  const handleSave = async () => {
    if (!form.name?.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError(null);
    try {
      let result: Client;
      if (mode === 'create') {
        result = await clientsApi.create(form);
      } else {
        result = await clientsApi.update(client!.id, form);
      }
      qc.invalidateQueries({ queryKey: ['clients'] });
      onSaved(result);
    } catch {
      // Fallback mock save
      const saved: Client = { id: client?.id || Date.now(), createdAt: client?.createdAt || new Date().toISOString(), ...form } as Client;
      onSaved(saved);
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ key2, label, placeholder, required, span }: { key2: keyof Client; label: string; placeholder?: string; required?: boolean; span?: boolean }) => (
    <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
      <label style={labelStyle}>{label}{required && ' *'}</label>
      {key2 === 'notes' ? (
        <textarea
          style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
          placeholder={placeholder}
          value={(form[key2] as string) || ''}
          onChange={e => setForm(f => ({ ...f, [key2]: e.target.value }))}
          readOnly={!isEditing}
        />
      ) : (
        <input
          style={{ ...inputStyle, cursor: isEditing ? 'text' : 'default', opacity: !isEditing && !form[key2] ? 0.5 : 1 }}
          placeholder={isEditing ? placeholder : '—'}
          value={(form[key2] as string) || ''}
          onChange={e => setForm(f => ({ ...f, [key2]: e.target.value }))}
          readOnly={!isEditing}
        />
      )}
    </div>
  );

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, backdropFilter: 'blur(4px)' }} />
      {/* Modal */}
      <div style={{ position: 'fixed', top: isMobile ? 0 : '50%', left: isMobile ? 0 : '50%', transform: isMobile ? 'none' : 'translate(-50%, -50%)', zIndex: 201, width: isMobile ? '100%' : '90%', maxWidth: isMobile ? 'none' : 620, height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '90vh', overflowY: 'auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: isMobile ? 0 : 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: `${color}22`, border: `2px solid ${color}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color, fontFamily: 'Syne, sans-serif', flexShrink: 0 }}>
            {client ? getInitials(client.name) : <User size={18} color={C.orange} />}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>
              {mode === 'create' ? 'Nuevo Cliente' : isEditing ? 'Editar Cliente' : client?.name}
            </h2>
            {mode !== 'create' && client?.company && (
              <p style={{ fontSize: 12, color: C.textSecondary, margin: 0 }}>{client.company}</p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {mode === 'view' && (
              <button onClick={() => setMode('edit')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, cursor: 'pointer', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                <Edit2 size={13} /> Editar
              </button>
            )}
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSecondary }}>
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <Field key2="name" label="NOMBRE" placeholder="Carlos García" required span />
          <Field key2="company" label="EMPRESA" placeholder="Moda Express SL" />
          <Field key2="nif" label="NIF/CIF" placeholder="B12345678" />
          <Field key2="email" label="EMAIL" placeholder="carlos@empresa.es" />
          <Field key2="phone" label="TELÉFONO" placeholder="612 345 678" />
          <Field key2="city" label="CIUDAD" placeholder="Madrid" />
          <Field key2="address" label="DIRECCIÓN" placeholder="Calle Gran Vía 10" />
          <Field key2="postalCode" label="CÓDIGO POSTAL" placeholder="28013" />
          <Field key2="country" label="PAÍS" placeholder="España" />
          <Field key2="notes" label="NOTAS" placeholder="Notas sobre el cliente..." span />
        </div>

        {error && (
          <div style={{ margin: '0 24px 16px', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, color: C.error }}>
            {error}
          </div>
        )}

        {/* Footer */}
        {isEditing && (
          <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '9px 22px', borderRadius: 9, border: 'none',
              background: saving ? '#2a2a3a' : `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`,
              color: saving ? C.textMuted : '#fff', fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
              boxShadow: saving ? 'none' : '0 4px 14px rgba(249,115,22,0.35)',
            }}>
              {saving ? (
                <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              ) : <Save size={14} />}
              {saving ? 'Guardando...' : mode === 'create' ? 'Crear Cliente' : 'Guardar Cambios'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Client History Panel ─────────────────────────────────────────────────────

type HistoryTab = 'datos' | 'proyectos' | 'facturas' | 'resumen';

const TABS: { id: HistoryTab; label: string; icon: React.ReactNode }[] = [
  { id: 'datos',     label: 'Datos',      icon: <User size={14} /> },
  { id: 'proyectos', label: 'Proyectos',  icon: <FolderKanban size={14} /> },
  { id: 'facturas',  label: 'Facturas',   icon: <FileText size={14} /> },
  { id: 'resumen',   label: 'Resumen',    icon: <BarChart3 size={14} /> },
];

function ClientHistoryPanel({
  client,
  onClose,
  onEdit,
  isMobile,
}: {
  client: Client;
  onClose: () => void;
  onEdit: () => void;
  isMobile: boolean;
}) {
  const [tab, setTab] = useState<HistoryTab>('datos');
  const color = avatarColor(client.id);

  const { data: history, isLoading, isError } = useQuery<ClientHistory>({
    queryKey: ['client-history', client.id],
    queryFn: () => clientsApi.history(client.id),
    retry: false,
  });

  const slideStyle: React.CSSProperties = isMobile
    ? { position: 'fixed', inset: 0, zIndex: 201, background: C.card, overflowY: 'auto' }
    : {
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 201,
        width: 560, background: C.card,
        borderLeft: `1px solid ${C.border}`,
        boxShadow: '-12px 0 40px rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
      };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, backdropFilter: 'blur(3px)' }} />
      <div style={slideStyle}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div style={{ width: 46, height: 46, borderRadius: '50%', background: `${color}22`, border: `2px solid ${color}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color, fontFamily: 'Syne, sans-serif', flexShrink: 0 }}>
            {getInitials(client.name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17, color: '#fff', margin: 0, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {client.name}
            </h2>
            {client.company && <p style={{ fontSize: 12, color: C.textSecondary, margin: 0 }}>{client.company}</p>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={onEdit} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, cursor: 'pointer', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
              <Edit2 size={13} /> Editar
            </button>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSecondary }}>
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0, padding: '0 24px' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '12px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: 13, fontFamily: 'DM Sans, sans-serif', fontWeight: tab === t.id ? 700 : 400,
                color: tab === t.id ? C.orange : C.textSecondary,
                borderBottom: `2px solid ${tab === t.id ? C.orange : 'transparent'}`,
                marginBottom: -1, transition: 'all 0.15s',
              }}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
              <div style={{ width: 28, height: 28, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
          )}

          {isError && (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <AlertCircle size={32} color={C.error} style={{ marginBottom: 12 }} />
              <p style={{ color: C.textSecondary, fontSize: 13 }}>No se pudo cargar el historial</p>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              {tab === 'datos' && <TabDatos client={history?.client ?? client} />}
              {tab === 'proyectos' && <TabProyectos projects={history?.projects ?? []} />}
              {tab === 'facturas' && <TabFacturas invoices={history?.invoices ?? []} />}
              {tab === 'resumen' && <TabResumen stats={history?.stats} />}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Tab: Datos ───────────────────────────────────────────────────────────────

function TabDatos({ client }: { client: Client }) {
  const fields: { label: string; value: string | undefined }[] = [
    { label: 'NIF/CIF',        value: client.nif },
    { label: 'Email',          value: client.email },
    { label: 'Teléfono',       value: client.phone },
    { label: 'Dirección',      value: client.address },
    { label: 'Ciudad',         value: client.city },
    { label: 'Código postal',  value: client.postalCode },
    { label: 'País',           value: client.country },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {fields.map(f => (
            <div key={f.label}>
              <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 4 }}>{f.label.toUpperCase()}</div>
              <div style={{ fontSize: 13, color: f.value ? C.textPrimary : C.textMuted, fontFamily: 'DM Sans, sans-serif' }}>{f.value || '—'}</div>
            </div>
          ))}
        </div>
      </div>
      {client.notes && (
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 8 }}>NOTAS</div>
          <p style={{ fontSize: 13, color: C.textSecondary, margin: 0, lineHeight: 1.6 }}>{client.notes}</p>
        </div>
      )}
      <div style={{ fontSize: 11, color: C.textMuted, textAlign: 'center' }}>
        Cliente desde {formatDate(client.createdAt)}
      </div>
    </div>
  );
}

// ─── Tab: Proyectos ───────────────────────────────────────────────────────────

function TabProyectos({ projects }: { projects: ClientHistoryProject[] }) {
  if (projects.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 16px' }}>
        <FolderKanban size={36} color={C.textMuted} style={{ marginBottom: 12 }} />
        <p style={{ color: C.textMuted, fontSize: 13 }}>No hay proyectos asociados</p>
      </div>
    );
  }

  const PRIORITY_COLOR: Record<string, string> = {
    low: C.textMuted, normal: C.info, high: C.warning, urgent: C.error,
  };
  const PRIORITY_LABEL: Record<string, string> = {
    low: 'Baja', normal: 'Normal', high: 'Alta', urgent: 'Urgente',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {projects.map(p => {
        const st = PROJECT_STATUS[p.status] ?? PROJECT_STATUS['pending'];
        return (
          <div key={p.id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                {p.reference && (
                  <div style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>{p.reference}</div>
                )}
              </div>
              <StatusBadge label={st.label} color={st.color} bg={st.bg} />
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: PRIORITY_COLOR[p.priority] ?? C.textMuted }} />
                <span style={{ fontSize: 11, color: C.textSecondary }}>{PRIORITY_LABEL[p.priority] ?? p.priority}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Calendar size={11} color={C.textMuted} />
                <span style={{ fontSize: 11, color: C.textMuted }}>{formatDate(p.createdAt)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <ChevronRight size={11} color={C.textMuted} />
                <span style={{ fontSize: 11, color: C.textMuted }}>{p.stepsCount} paso{p.stepsCount !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab: Facturas ────────────────────────────────────────────────────────────

function TabFacturas({ invoices }: { invoices: ClientHistoryInvoice[] }) {
  if (invoices.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 16px' }}>
        <FileText size={36} color={C.textMuted} style={{ marginBottom: 12 }} />
        <p style={{ color: C.textMuted, fontSize: 13 }}>No hay facturas asociadas</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {invoices.map(inv => {
        const st = INVOICE_STATUS[inv.status] ?? INVOICE_STATUS['draft'];
        return (
          <div key={inv.id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.orange, fontFamily: 'JetBrains Mono, monospace' }}>{inv.number}</div>
              <StatusBadge label={st.label} color={st.color} bg={st.bg} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Calendar size={11} color={C.textMuted} />
                  <span style={{ fontSize: 11, color: C.textMuted }}>{formatDate(inv.issueDate)}</span>
                </div>
                {inv.dueDate && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Clock size={11} color={C.textMuted} />
                    <span style={{ fontSize: 11, color: C.textMuted }}>Vence {formatDate(inv.dueDate)}</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, fontFamily: 'Syne, sans-serif' }}>
                {formatEuro(inv.total)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab: Resumen ─────────────────────────────────────────────────────────────

function TabResumen({ stats }: { stats?: ClientHistory['stats'] }) {
  if (!stats) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <div style={{ width: 28, height: 28, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total proyectos',
      value: stats.totalProjects.toString(),
      icon: <FolderKanban size={20} color={C.info} />,
      color: C.info,
    },
    {
      label: 'Proyectos completados',
      value: stats.completedProjects.toString(),
      icon: <CheckCircle2 size={20} color={C.success} />,
      color: C.success,
    },
    {
      label: 'Total facturado',
      value: formatEuro(stats.totalInvoiced),
      icon: <TrendingUp size={20} color={C.orange} />,
      color: C.orange,
    },
    {
      label: 'Facturas pendientes',
      value: stats.pendingInvoices.toString(),
      icon: <Clock size={20} color={C.warning} />,
      color: C.warning,
    },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {statCards.map(s => (
        <div key={s.label} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 14, padding: '18px 16px' }}>
          <div style={{ marginBottom: 10 }}>{s.icon}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: 'Syne, sans-serif', marginBottom: 4 }}>{s.value}</div>
          <div style={{ fontSize: 11, color: C.textSecondary, fontFamily: 'DM Sans, sans-serif' }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Main ClientsPage ─────────────────────────────────────────────────────────

export default function ClientsPage() {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [selected, setSelected] = useState<Client | null>(null);
  const [panelMode, setPanelMode] = useState<'history' | 'edit' | 'create' | null>(null);
  const [extraClients, setExtraClients] = useState<Client[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  useEffect(() => {
    const handler = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data: apiClients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list(),
    retry: false,
  });

  const baseClients: Client[] = (Array.isArray(apiClients) && apiClients.length > 0) ? apiClients : MOCK_CLIENTS;
  const allClients = useMemo(() => {
    const ids = new Set(baseClients.map(c => c.id));
    return [...baseClients, ...extraClients.filter(c => !ids.has(c.id))];
  }, [baseClients, extraClients]);

  const filtered = useMemo(() => {
    if (!search) return allClients;
    const q = search.toLowerCase();
    return allClients.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.company || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q)
    );
  }, [allClients, search]);

  const openClient = (c: Client) => { setSelected(c); setPanelMode('history'); };

  const handleSaved = (c: Client) => {
    setExtraClients(prev => {
      const exists = prev.some(x => x.id === c.id);
      return exists ? prev.map(x => x.id === c.id ? c : x) : [...prev, c];
    });
    setPanelMode(null);
    setSelected(null);
  };

  const closePanel = () => { setPanelMode(null); setSelected(null); };

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: C.bg, fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'flex-start',
        gap: isMobile ? 16 : 0,
        marginBottom: 28,
      }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: isMobile ? 24 : 28, color: '#fff', margin: '0 0 6px', letterSpacing: '-0.03em' }}>Clientes</h1>
          <p style={{ fontSize: 13, color: C.textSecondary, margin: 0 }}>
            {filtered.length} cliente{filtered.length !== 1 ? 's' : ''} registrado{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => { setSelected(null); setPanelMode('create'); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`, border: 'none', borderRadius: 10, padding: '10px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, boxShadow: '0 4px 14px rgba(249,115,22,0.35)', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : undefined }}
        >
          <Plus size={16} /> Nuevo Cliente
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 16px', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 200px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px' }}>
          <Search size={14} color={C.textSecondary} />
          <input
            type="text"
            placeholder="Buscar por nombre, empresa, email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 13, width: '100%', fontFamily: 'DM Sans, sans-serif' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: 0 }}>
              <X size={13} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginLeft: 'auto' }}>
          <button onClick={() => setView('grid')} style={{ padding: '8px 12px', background: view === 'grid' ? 'rgba(249,115,22,0.15)' : 'transparent', border: 'none', cursor: 'pointer', color: view === 'grid' ? C.orange : C.textSecondary, transition: 'all 0.15s' }}>
            <Grid3X3 size={15} />
          </button>
          <button onClick={() => setView('list')} style={{ padding: '8px 12px', background: view === 'list' ? 'rgba(249,115,22,0.15)' : 'transparent', border: 'none', cursor: 'pointer', color: view === 'list' ? C.orange : C.textSecondary, transition: 'all 0.15s' }}>
            <List size={15} />
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : view === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filtered.map(c => <ClientGridCard key={c.id} client={c} onClick={() => openClient(c)} />)}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1/-1', padding: '48px 16px', textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
              No se encontraron clientes
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Cliente', 'Email', 'Teléfono', 'Ciudad', 'Alta'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', fontWeight: 600 }}>
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => <ClientListRow key={c.id} client={c} onClick={() => openClient(c)} />)}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '48px 16px', textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
                    No se encontraron clientes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* History panel */}
      {panelMode === 'history' && selected && (
        <ClientHistoryPanel
          client={selected}
          onClose={closePanel}
          onEdit={() => setPanelMode('edit')}
          isMobile={isMobile}
        />
      )}

      {/* Edit / Create modal */}
      {(panelMode === 'edit' || panelMode === 'create') && (
        <ClientModal
          client={selected || undefined}
          mode={panelMode === 'create' ? 'create' : 'edit'}
          onClose={() => {
            if (panelMode === 'edit' && selected) {
              setPanelMode('history');
            } else {
              closePanel();
            }
          }}
          onSaved={handleSaved}
          isMobile={isMobile}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
